import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI } from '../utils/api';

const STATUS_CONFIG = {
  new: { label: 'Application Received', color: '#2563EB', bg: '#EFF6FF', step: 1 },
  reviewing: { label: 'Under Review', color: '#7C3AED', bg: '#F5F3FF', step: 2 },
  shortlisted: { label: 'Shortlisted', color: '#059669', bg: '#F0FDF4', step: 3 },
  questionnaire_sent: { label: 'Questionnaire Sent', color: '#D97706', bg: '#FFFBEB', step: 3 },
  questionnaire_completed: { label: 'Questionnaire Completed', color: '#059669', bg: '#F0FDF4', step: 4 },
  screening_call: { label: 'Screening Call', color: '#7C3AED', bg: '#F5F3FF', step: 4 },
  screening_passed: { label: 'Screening Passed', color: '#059669', bg: '#F0FDF4', step: 4 },
  interview: { label: 'Interview Stage', color: '#2563EB', bg: '#EFF6FF', step: 5 },
  assessment_sent: { label: 'Assessment Sent', color: '#D97706', bg: '#FFFBEB', step: 5 },
  assessment_completed: { label: 'Assessment Completed', color: '#059669', bg: '#F0FDF4', step: 6 },
  offer: { label: 'Offer Extended', color: '#059669', bg: '#F0FDF4', step: 7 },
  accepted: { label: 'Offer Accepted', color: '#059669', bg: '#F0FDF4', step: 8 },
  hired: { label: 'Hired', color: '#059669', bg: '#F0FDF4', step: 9 },
  rejected: { label: 'Not Selected', color: '#DC2626', bg: '#FEF2F2', step: -1 },
  withdrawn: { label: 'Withdrawn', color: '#6B7280', bg: '#F9FAFB', step: -1 },
  declined: { label: 'Offer Declined', color: '#6B7280', bg: '#F9FAFB', step: -1 }
};

const ApplicationTracker = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    if (uuid) loadApplication();
  }, [uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadApplication = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await jobsAPI.getApplicationStatus(uuid);
      setApplication(response.data.application);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load application. Please check your tracking link.');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (withdrawing) return;
    setWithdrawing(true);
    try {
      await jobsAPI.withdrawApplication(uuid, withdrawReason.trim() || undefined);
      setShowWithdraw(false);
      loadApplication();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to withdraw application');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusCfg = application ? (STATUS_CONFIG[application.status] || STATUS_CONFIG.new) : null;
  const isTerminal = application && ['hired', 'rejected', 'withdrawn', 'declined'].includes(application.status);

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', padding: '1.5rem 2rem', textAlign: 'center' }}>
        <h1 style={{ color: '#fff', margin: 0, fontSize: '1.5rem', fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/')}>SecHire</h1>
        <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Application Tracker</p>
      </div>

      <div style={{ maxWidth: '680px', margin: '2rem auto', padding: '0 1rem' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6B7280' }}>
            <p>Loading application status...</p>
          </div>
        )}

        {error && !loading && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
            <p style={{ color: '#991B1B', margin: 0, fontWeight: 500 }}>{error}</p>
            <button onClick={() => navigate('/')} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', background: '#1E40AF', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>
              Back to Home
            </button>
          </div>
        )}

        {application && !loading && (
          <>
            {/* Status Card */}
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', marginBottom: '1.5rem' }}>
              <div style={{ background: statusCfg.bg, padding: '1.5rem', borderBottom: `3px solid ${statusCfg.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <span style={{ fontSize: '0.6875rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Status</span>
                    <h2 style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', color: statusCfg.color, fontWeight: 700 }}>{statusCfg.label}</h2>
                  </div>
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, color: statusCfg.color, background: '#fff', border: `1px solid ${statusCfg.color}30` }}>
                    {application.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Position</span>
                    <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827', marginTop: '0.125rem' }}>{application.jobTitle}</div>
                  </div>
                  {application.jobLocation && (
                    <div>
                      <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Location</span>
                      <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.125rem' }}>{application.jobLocation}</div>
                    </div>
                  )}
                  <div>
                    <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Applied</span>
                    <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.125rem' }}>{formatDate(application.appliedAt)}</div>
                  </div>
                  {application.lastActivityAt && (
                    <div>
                      <span style={{ fontSize: '0.6875rem', color: '#9CA3AF', textTransform: 'uppercase' }}>Last Update</span>
                      <div style={{ fontSize: '0.875rem', color: '#374151', marginTop: '0.125rem' }}>{formatDate(application.lastActivityAt)}</div>
                    </div>
                  )}
                </div>

                {/* Verification badges */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', background: application.emailVerified ? '#F0FDF4' : '#FEF2F2', color: application.emailVerified ? '#059669' : '#DC2626', border: `1px solid ${application.emailVerified ? '#059669' : '#DC2626'}20` }}>
                    {application.emailVerified ? 'Email Verified' : 'Email Not Verified'}
                  </span>
                  {application.phoneVerified !== undefined && (
                    <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', background: application.phoneVerified ? '#F0FDF4' : '#F9FAFB', color: application.phoneVerified ? '#059669' : '#6B7280', border: `1px solid ${application.phoneVerified ? '#059669' : '#6B7280'}20` }}>
                      {application.phoneVerified ? 'Phone Verified' : 'Phone Not Verified'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Timeline */}
            {application.timeline && application.timeline.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Application Timeline</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {application.timeline
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((entry, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '0.75rem', paddingLeft: '0.5rem', borderLeft: '2px solid #E5E7EB' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3B82F6', marginTop: '0.375rem', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#111827', textTransform: 'capitalize' }}>{entry.action}</div>
                          {entry.description && <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.125rem' }}>{entry.description}</div>}
                          <div style={{ fontSize: '0.6875rem', color: '#9CA3AF', marginTop: '0.125rem' }}>{formatDate(entry.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Withdraw section */}
            {!isTerminal && (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '1.5rem' }}>
                {!showWithdraw ? (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '0.8125rem', color: '#6B7280', margin: '0 0 0.75rem' }}>Changed your mind?</p>
                    <button onClick={() => setShowWithdraw(true)} style={{ padding: '0.5rem 1.25rem', background: 'none', color: '#DC2626', border: '1px solid #DC2626', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500 }}>
                      Withdraw Application
                    </button>
                  </div>
                ) : (
                  <div>
                    <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', color: '#DC2626', fontWeight: 600 }}>Withdraw Application</h3>
                    <p style={{ fontSize: '0.8125rem', color: '#6B7280', margin: '0 0 0.75rem' }}>This action cannot be undone. You may re-apply later.</p>
                    <textarea
                      value={withdrawReason}
                      onChange={(e) => setWithdrawReason(e.target.value)}
                      placeholder="Reason for withdrawal (optional)"
                      rows={3}
                      style={{ width: '100%', padding: '0.625rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: '0.75rem' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button onClick={() => setShowWithdraw(false)} disabled={withdrawing} style={{ padding: '0.5rem 1rem', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8125rem' }}>
                        Cancel
                      </button>
                      <button onClick={handleWithdraw} disabled={withdrawing} style={{ padding: '0.5rem 1rem', background: withdrawing ? '#D1D5DB' : '#DC2626', color: '#fff', border: 'none', borderRadius: '6px', cursor: withdrawing ? 'not-allowed' : 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}>
                        {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationTracker;
